/*
 * Copyright 2025 Intuition Robotics ltd
 */
import __asset_0 from "@res/icons/btn_toggle_off_outline.svg";
import __asset_1 from "@res/icons/btn_toggle_on_outline.svg";
import __asset_2 from "@res/icons/rsvp_cancellation.svg";
import __asset_3 from "@res/icons/rsvp_confirmed.svg";
import __asset_4 from "@res/icons/rsvp_pending.svg";
import __asset_5 from "@res/icons/rsvp_reschedule.svg";
import {BaseComponent} from "@intuitionrobotics/thunderstorm/frontend";
import * as React from "react";
import {Spacing} from "@styles/styles";
import {DB_Patient} from "@app/app-shared/patient";
import {FONTS} from "@res/fonts";
import {VGap} from "../widget/VGap";
import {Card} from "../widget/Card";
import * as emotion from "emotion";
import {HLine} from "../widget/HLine";
import {COLORS} from "@res/colors";
import {IconInfo} from "../widget/IconInfo";
import {
	hideTooltip,
	showTooltip
} from "../widget/appTooltip";
import {Badge} from "../widget/Badge";
import {
	Appointment,
	AppointmentConfigFields,
	RSVP
} from "@app/app-shared/appointment";
import {
	AppointmentModule,
	OnAppointmentUpdated
} from "@modules/AppointmentModule";
import {LoadingBar} from "../widget/LoadingBar";

const toggleClass = emotion.css(
	{
		cursor: "pointer",
		marginRight: Spacing.m
	}
);

const disabledClass = emotion.css(
	{
		cursor: "unset",
		filter: "grayscale(100%)",
		pointerEvents: "none"
	}
);

const lockedClass = emotion.css(
	{
		cursor: "unset",
		opacity: 0.8,
		pointerEvents: "none"
	}
);

const itemClass = emotion.css(
	{
		boxSizing: "border-box",
		paddingTop: 25,
		paddingBottom: 25
	}
);

const off = __asset_0;
const on = __asset_1;
const rsvp_cancellation = __asset_2;
const rsvp_confirmed = __asset_3;
const rsvp_pending = __asset_4;
const rsvp_reschedule = __asset_5;

type Props = {
	patient: DB_Patient;
}

type State = {
	loading: boolean;
}

export class AppointmentInfoWrapper
	extends BaseComponent<Props, State>
	implements OnAppointmentUpdated {

	private readonly rsvpFields: Record<RSVP, { icon: () => React.JSX.Element, text: string }> = {
		[RSVP.PENDING]: {
			icon: () => <img alt={"pending"} src={rsvp_pending} style={{width: 12}}/>,
			text: "Pending RSVP"
		},
		[RSVP.CONFIRMED]: {
			icon: () => <img alt={"pending"} src={rsvp_confirmed} style={{width: 16}}/>,
			text: "Confirmed"
		},
		[RSVP.RESCHEDULE]: {
			icon: () => <img alt={"pending"} src={rsvp_reschedule} style={{width: 15}}/>,
			text: "Reschedule"
		},
		[RSVP.CANCEL]: {
			icon: () => <img alt={"pending"} src={rsvp_cancellation} style={{width: 16}}/>,
			text: "Cancellation requested"
		}
	};

	private readonly fields: Record<AppointmentConfigFields, {
		info: string,
		text: string,
		extraRender?: (appointment?: Appointment) => React.JSX.Element | null,
		isLocked?: (appointment?: Appointment) => boolean,
	}> = {
		appointmentReminder: {
			text: "Remind customer of upcoming appointment and RSVP",
			info: "When enabled, ElliQ will attempt to remind the customer about the appointment and collect an RSVP",
			extraRender: (appointment) => {
				if (!appointment)
					return null;
				const enabled = appointment?.metadata?.appointmentReminder || false;
				const rsvp = appointment?.metadata?.rsvp || RSVP.PENDING;
				return (enabled) ? (
					<div className={`ll_h_c`} style={{marginLeft: "auto"}}>
						{this.rsvpFields[rsvp].icon()}
						{FONTS.semi_bold_14(this.rsvpFields[rsvp].text, {marginLeft: 10})}
					</div>
				) : null;
			},
			isLocked: (appointment?: Appointment) => {
				if (!appointment)
					return false;
				return appointment.metadata?.rsvp !== RSVP.PENDING;
			}
		},
		medicationReview: {
			text: "Ask for a medication review before the next appointment",
			info: "When enabled, ElliQ will ask the user to record a video of the medication they are taking",
			extraRender: (appointment) => {
				if (!appointment)
					return null;
				const uploaded = appointment?.metadata?.videoUploaded || false;
				return (uploaded) ? (
					<div className={`ll_h_c`} style={{marginLeft: "auto"}}>
						{this.rsvpFields[RSVP.CONFIRMED].icon()}
						{FONTS.semi_bold_14("Uploaded", {marginLeft: 10})}
					</div>
				) : null;
			},
			isLocked: (appointment?: Appointment) => {
				if (!appointment)
					return false;
				return appointment.metadata?.videoUploaded || false;
			}
		}
	};

	constructor(props: Props) {
		super(props);
		this.state = {loading: false};
	}

	__onAppointmentUpdated = () => {
		this.setState({loading: false});
		this.forceUpdate();
	};

	render() {
		if (!AppointmentModule.isEnabled())
			return null;

		return (
			<div className={`ll_v_s`} style={{paddingBottom: 20}}>
				<div className={`ll_h_c`} style={{height: "1.2em"}}>
					{FONTS.reg_18("Appointments Information")}
				</div>
				<VGap v={Spacing.m}/>
				<Card
					style={
						{
							opacity: this.state.loading ? 0.8 : 1,
							position: "relative",
							padding: `${Spacing.xs}px ${3 * Spacing.s}px`
						}
					}
				>
					{<LoadingBar status={this.state.loading ? "loading" : (AppointmentModule.hasError() ? "error" : "success")}/>}
					{this.renderToggles()}
				</Card>
			</div>
		);
	}

	private renderToggles() {
		const appointment = AppointmentModule.getNextAppointment(this.props.patient);
		return (
			<>
				{
					(Object.keys(this.fields) as Array<AppointmentConfigFields>).map((key, i) => {
						const enabled = appointment?.metadata?.[key];
						const locked = this.fields[key]?.isLocked?.(appointment) || false;
						return (
							<>
								{i !== 0 ? <HLine style={{backgroundColor: COLORS.color_gray_medium}}/> : null}
								<div className={`ll_h_c ${itemClass}`}>
									<div
										className={`ll_h_c ${toggleClass} ${(appointment === undefined || this.state.loading) ? disabledClass : ""} ${locked ? lockedClass : ""}`}
										onClick={() => {
											if (!appointment)
												return;
											this.setState({loading: true});
											AppointmentModule.updateAppointmentConfig(key, !(Boolean(appointment?.metadata?.[key])), this.props.patient);
										}}
									>
										{
											enabled ? <img alt="on" src={on}/> : <img alt="off" src={off}/>
										}
									</div>
									<Badge show={true} top={6} right={-3} badge={
										<IconInfo
											onMouseOver={(e) => {
												const rect = e.currentTarget.getBoundingClientRect();
												showTooltip(
													e, FONTS.reg_14_secondary_dark(this.fields[key].info),
													rect.left - 150,
													rect.top - 5,
													{
														backgroundColor: COLORS.white,
														padding: 8,
														width: 300,
														transform: "translate(0%, -105%)",
														border: `1px solid ${COLORS.primary_light_blue}`
													});
											}}
											onMouseLeave={() => {
												hideTooltip();
											}}/>
									}>
										<div className={`ll_h_c`}>
											{FONTS.reg_16_dark(this.fields[key].text)}
										</div>
									</Badge>
									{this.fields[key].extraRender?.(appointment) ?? null}
								</div>
							</>
						);
					})
				}
			</>
		);
	}
}
